import { c as _c } from "react/compiler-runtime";
import type { ComponentProps, ReactNode } from 'react';
import { cn } from 'cn';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
interface ConditionalTooltipProps {
  /** When false, render children directly without tooltip markup. */
  enabled: boolean;
  /** Trigger element. */
  children: ReactNode;
  /** Tooltip body. */
  content: ReactNode;
  /** Additional classes applied to the trigger wrapper. */
  triggerClassName?: string;
  /** Classes applied to the tooltip content. */
  contentClassName?: string;
  /** Tooltip content alignment. */
  align?: ComponentProps<typeof TooltipContent>['align'];
  /** Pixel shift along the alignment axis. Negative moves a top tooltip left. */
  alignOffset?: ComponentProps<typeof TooltipContent>['alignOffset'];
  /** Tooltip content side. */
  side?: ComponentProps<typeof TooltipContent>['side'];
  /** Minimum gap (px) kept between the tooltip and the viewport edge. */
  collisionPadding?: ComponentProps<typeof TooltipContent>['collisionPadding'];
  /** Content size variant (e.g. `small` for text-xs). */
  variant?: ComponentProps<typeof TooltipContent>['variant'];
  /** Tooltip provider delay duration in ms. */
  delayDuration?: ComponentProps<typeof TooltipProvider>['delayDuration'];
  /**
   * Whether the trigger is a disabled control. Disabled elements fire no
   * pointer/focus events, so they're wrapped in a focusable `cursor-not-allowed`
   * span that anchors the tooltip. Set false for an enabled trigger to anchor
   * the tooltip directly on the child (via `asChild`) without the extra span.
   */
  triggerDisabled?: boolean;
}

/**
 * Conditionally wraps children in a Tooltip. Useful for disabled controls that
 * need an explanatory hover/focus message without duplicating markup, or for an
 * enabled control whose tooltip should appear only under a condition
 * (`triggerDisabled={false}`).
 */
export function ConditionalTooltip(t0) {
  const $ = _c(22);
  const {
    enabled,
    children,
    content,
    triggerClassName,
    contentClassName,
    align: t1,
    alignOffset,
    side,
    collisionPadding,
    variant,
    delayDuration: t2,
    triggerDisabled: t3
  } = t0;
  const align = t1 === undefined ? "center" : t1;
  const delayDuration = t2 === undefined ? 150 : t2;
  const triggerDisabled = t3 === undefined ? true : t3;
  if (!enabled) {
    return children;
  }
  let t4;
  if ($[0] !== children || $[1] !== triggerClassName || $[2] !== triggerDisabled) {
    t4 = triggerDisabled ? <span className={cn("inline-flex cursor-not-allowed", triggerClassName)} tabIndex={0}>{children}</span> : children;
    $[0] = children;
    $[1] = triggerClassName;
    $[2] = triggerDisabled;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t4) {
    t5 = <TooltipTrigger asChild={true} data-sentry-element="TooltipTrigger" data-sentry-source-file="conditional-tooltip.tsx">{t4}</TooltipTrigger>;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== contentClassName) {
    t6 = cn("max-w-64", contentClassName);
    $[6] = contentClassName;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== align || $[9] !== alignOffset || $[10] !== collisionPadding || $[11] !== content || $[12] !== side || $[13] !== t6 || $[14] !== variant) {
    t7 = <TooltipContent align={align} alignOffset={alignOffset} side={side} collisionPadding={collisionPadding} variant={variant} className={t6} data-sentry-element="TooltipContent" data-sentry-source-file="conditional-tooltip.tsx">{content}</TooltipContent>;
    $[8] = align;
    $[9] = alignOffset;
    $[10] = collisionPadding;
    $[11] = content;
    $[12] = side;
    $[13] = t6;
    $[14] = variant;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t5 || $[17] !== t7) {
    t8 = <Tooltip data-sentry-element="Tooltip" data-sentry-source-file="conditional-tooltip.tsx">{t5}{t7}</Tooltip>;
    $[16] = t5;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== delayDuration || $[20] !== t8) {
    t9 = <TooltipProvider delayDuration={delayDuration} data-sentry-element="TooltipProvider" data-sentry-component="ConditionalTooltip" data-sentry-source-file="conditional-tooltip.tsx">{t8}</TooltipProvider>;
    $[19] = delayDuration;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
}